import { useBunja } from 'bunja/react';
import Link from 'next/link';
import { useState } from 'react';

import { FollowSuggestion } from '@flipster/apis/v1/community/types';
import { Badge, Button } from '@flipster/react/design-system';
import { snackbar } from '@flipster/react/design-system/snackbar';
import Formatted from '@flipster/react/design-utils/formatted';
import ArrowTrendingDownIcon from '@flipster/react/icon/generated/ArrowTrendingDownIcon';
import ArrowTrendingUpIcon from '@flipster/react/icon/generated/ArrowTrendingUpIcon';
import CrownIcon from '@flipster/react/icon/generated/CrownIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import { getErrorMessage } from '@flipster/utils/error';
import segment from '@flipster/utils/segment';
import { CommunityEvent } from '@flipster/utils/segment/events/community';
import { V2OrderSideType } from '@flipster/utils/types/base';

import { SymbolDisplay } from '../../../../misc/display';
import { followOnboardingBunja } from './bunja';

interface FollowCardProps {
  suggestion: FollowSuggestion;
}
export const FollowCard = ({ suggestion }: FollowCardProps) => {
  const { followUser, unfollowUser } = useBunja(followOnboardingBunja);
  const [following, setFollowing] = useState(false);
  return (
    <div
      className={flex({
        p: 20,
        bg: 'background.default.level-0',
        rounded: 8,
        gap: {
          base: 24,
          tablet: 16,
        },
        tabletDown: {
          flexDirection: 'column',
        },
      })}
    >
      <div className={stack({ gap: { base: 16, tablet: 12 }, flex: 1 })}>
        <div className={stack({ gap: 8, align: 'flex-start' })}>
          <Badge type='tint' color='accent' leadingIcon={<CrownIcon />}>
            Weekly rank #{suggestion.weeklyLeaderboardRank}
          </Badge>
          <div className={flex({ gap: { base: 4, tablet: 8 }, tabletDown: { flexDirection: 'column' } })}>
            <Link href={`/user/profile/${suggestion.profileId}`} target='_blank'>
              <p className={css({ textStyle: 'Title/XS', color: 'content.default.level-4' })}>{suggestion.nickname}</p>
            </Link>
            <Formatted.Number
              value={suggestion.weeklyLeaderboardPnl}
              currency={UsdtCurrency}
              precision={UsdtPrecision}
              withPlus
              withColor
              withComma
              toFixed
              css={{ textStyle: 'Title/XS/Monospace' }}
            />
          </div>
        </div>
        <div className={hstack({ gap: 8 })}>
          <p
            className={css({
              textStyle: 'Text/M/Regular',
              color: 'content.default.level-2',
              textAlign: 'right',
            })}
          >
            Recent trade
          </p>
          <div className={hstack({ gap: 4 })}>
            <SymbolDisplay
              symbol={suggestion.recentBestTrade.symbol}
              icon={<SymbolDisplay.Icon size={14} space={4} />}
              mainLabel={
                <SymbolDisplay.Label
                  value='shortName'
                  css={{ textStyle: 'Text/M/Strong', color: 'content.default.level-4' }}
                />
              }
            />
            <Side side={suggestion.recentBestTrade.side} />
          </div>
        </div>
      </div>
      {following ? (
        <Button
          hierarchy='secondary'
          color='neutral'
          size='medium'
          label='Following'
          css={{ w: { base: '100%', tablet: 100 } }}
          withSpinner
          onClick={async () => {
            segment.track(CommunityEvent.TopTradersModalFollowClick, {
              profile_id: suggestion.profileId,
              profile_username: suggestion.nickname,
              follow_type: 'unfollow',
            });
            try {
              await unfollowUser(suggestion.profileId);
            } catch (error) {
              snackbar.error({ message: getErrorMessage(error) });
            } finally {
              setFollowing((f) => !f);
            }
          }}
        />
      ) : (
        <Button
          hierarchy='primary'
          color='neutral'
          size='medium'
          label='Follow'
          css={{ w: { base: '100%', tablet: 100 } }}
          withSpinner
          onClick={async () => {
            segment.track(CommunityEvent.TopTradersModalFollowClick, {
              profile_id: suggestion.profileId,
              profile_username: suggestion.nickname,
              follow_type: 'follow',
            });
            try {
              await followUser(suggestion.profileId);
            } catch (error) {
              snackbar.error({ message: getErrorMessage(error) });
            } finally {
              setFollowing((f) => !f);
            }
          }}
        />
      )}
    </div>
  );
};

interface SideProps {
  side: V2OrderSideType;
}
const Side = ({ side }: SideProps) => {
  return (
    <div className={hstack({ gap: 2 })}>
      <p
        className={css({
          textStyle: 'Text/M/Regular',
          color: 'content.default.level-2',
          textAlign: 'right',
        })}
      >
        {side}
      </p>
      {side === 'Long' ? <ArrowTrendingUpIcon size={14} /> : <ArrowTrendingDownIcon size={14} />}
    </div>
  );
};
